import { DataTable } from '@/components/features/shared/DataTable'
import { formatPaymentMethodBadge, renderDatetime } from '@/components/features/shared/utils'
import { Badge } from '@/components/ui/badge'
import { IconButton } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PaginatedResponse } from '@/domain'
import { CashBoxMovement, formatMovementStatus } from '@/domain/accounts/account'
import { useUser } from '@/hooks/api/useUsers'
import { Paths } from '@/routes/paths'
import { firstLetterCapitalizeStr } from '@/utils/strings'
import { useNavigate } from '@tanstack/react-router'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { ArrowRight, Trash2Icon } from 'lucide-react'
import { RenderAmount, RenderBalance } from './account-utils'

interface CashBoxMovementsTableProps {
  paginated: PaginatedResponse<CashBoxMovement> | undefined
  isLoading?: boolean
  pagination?: PaginationState
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>
  onDeleteRequest?: (movement: CashBoxMovement) => void
}

export const CashBoxMovementsTable = ({
  paginated,
  isLoading,
  pagination,
  onPaginationChange,
  onDeleteRequest,
}: CashBoxMovementsTableProps) => {
  const navigate = useNavigate()
  const data = paginated?.data || []

  const columns: ColumnDef<CashBoxMovement>[] = [
    {
      accessorKey: 'description',
      header: 'Descripcion',
      size: 200,
      cell: ({ row }) => {
        const movement = row.original
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              {movement.status !== 'completed' && (
                <Badge variant="outline">{formatMovementStatus(movement.status as any)}</Badge>
              )}
              <span className="font-medium break-words">{movement.description}</span>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'customerCode',
      header: 'Origen',
      size: 140,
      cell: ({ row }) => {
        const movement = row.original
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{movement.customerCode || movement.accountNumber + ' - ' + firstLetterCapitalizeStr(movement.accountAlias)}</span>
            {movement.customerName && (
              <span className="text-xs text-muted-foreground">{movement.customerName}</span>
            )}
          </div>
        )
      },
    },
    // {
    //   accessorKey: 'contractNumber',
    //   header: 'Contrato',
    //   size: 120,
    //   cell: ({ row }) => {
    //     const movement = row.original
    //     return (
    //       <span className="text-sm">{movement.contractNumber || 'N/A'}</span>
    //     )
    //   },
    // },
    {
      accessorKey: 'movementDate',
      header: 'Fecha',
      size: 120,
      cell: ({ row }) => {
        const movement = row.original
        return renderDatetime(movement.movementDate)
      },
    },
    {
      accessorKey: 'paymentMethod',
      header: 'Método',
      size: 110,
      cell: ({ row }) => {
        const movement = row.original
        return (
          <div className={`w-[max-content]`}>
            {formatPaymentMethodBadge(movement.paymentMethod)}
          </div>
        )
      },
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      size: 110,
      cell: ({ row }) => {
        const movement = row.original
        return (
          <div className={`w-[max-content]`}>
            <RenderAmount amount={movement.amount} movementType={movement.movementType} />
          </div>
        )
      },
    },
    {
      accessorKey: 'balanceAfter',
      header: 'Saldo',
      size: 110,
      cell: ({ row }) => {
        const movement = row.original
        return (
          <div className="w-[max-content]">
            <RenderBalance balance={movement.balanceAfter} />
          </div>
        )
      },
    },
    {
      accessorKey: 'createdBy',
      header: 'Usuario',
      size: 150,
      cell: ({ row }) => {
        const movement = row.original
        const { data: user } = useUser(movement.createdBy)
        return (
          <span className="text-sm">{user?.fullName || '-'}</span>
        )
      },
    },
    {
      id: 'actions',
      header: 'Acciones',
      size: 80,
      cell: ({ row }) => {
        const movement = row.original
        return (
          <div className="flex items-center gap-2">
            {movement.customerId && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  navigate({ to: `${Paths.Management.Clients}/${movement.customerId}` })
                }}
                title="Ver cliente"
              >
                <ArrowRight className="h-3.5 w-3.5" />
              </IconButton>
            )}
            {movement.contractId && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  navigate({ to: `${Paths.Management.Contracts}/${movement.contractId}` })
                }}
              >
                <ArrowRight className="h-3.5 w-3.5" />
              </IconButton>
            )}
            {onDeleteRequest && (
              <IconButton
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation()
                  onDeleteRequest(movement)
                }}
              >
                <Trash2Icon className="h-3.5 w-3.5" />
              </IconButton>
            )}
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={data}
      pageCount={paginated?.totalPages}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      totalRecords={paginated?.total}
    />
  )
}
